'use client';

import { IconButton } from '@/components/iconButton';
import styles from '@/components/tabs/Tabs.module.scss';
import ArrowForwardIcon from '@/public/icons/pfeil-rechts-line.svg';
import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import classNames from 'classnames';
import { type ComponentProps, type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type TabsProps = ComponentProps<typeof TabsPrimitive.Root>;

export const Tabs: FC<TabsProps> = ({ className, orientation = 'horizontal', ...props }) => (
  <TabsPrimitive.Root
    className={classNames(styles.tabs, className)}
    data-orientation={orientation}
    data-slot="tabs"
    {...props}
  />
);

export type TabsListVariant = 'default' | 'line' | 'card' | 'unstyled';

export type TabsListProps = ComponentProps<typeof TabsPrimitive.List> & {
  scrollable?: boolean;
  variant?: TabsListVariant;
};

export const TabsList: FC<TabsListProps> = ({ className, variant = 'default', scrollable = false, ...props }) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const { t } = useTranslation();

  const checkScrollPosition = useCallback(() => {
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }

    const { scrollLeft, clientWidth, scrollWidth } = listElement;
    const overflowing = scrollWidth > clientWidth + 1;

    setIsOverflowing(overflowing);
    setCanScrollLeft(overflowing && scrollLeft > 1);
    setCanScrollRight(overflowing && scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    if (!scrollable) {
      return;
    }

    const listElement = listRef.current;
    if (!listElement) {
      return;
    }

    checkScrollPosition();
    listElement.addEventListener('scroll', checkScrollPosition, { passive: true });

    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(checkScrollPosition);
      resizeObserver.observe(listElement);
    }

    return () => {
      listElement.removeEventListener('scroll', checkScrollPosition);
      resizeObserver?.disconnect();
    };
  }, [scrollable, checkScrollPosition, props.children]);

  const scrollBy = (direction: 'left' | 'right') => {
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }

    const amount = listElement.clientWidth;
    listElement.scrollBy({ left: direction === 'left' ? -amount : amount });
  };

  const list = (
    <TabsPrimitive.List
      ref={scrollable ? listRef : undefined}
      className={classNames(styles.list, scrollable && styles.listScrollable, className)}
      data-slot="tabs-list"
      data-variant={variant}
      {...props}
    />
  );

  if (!scrollable) {
    return list;
  }

  return (
    <div
      className={classNames(styles.listScrollHost, isOverflowing && styles.listScrollHostOverflowing)}
      data-overflowing={isOverflowing || undefined}
      data-slot="tabs-list-scroll-host"
      data-variant={variant}
    >
      {list}
      {isOverflowing && (
        <IconButton
          aria-label={t('Tabs links scrollen')}
          className={classNames(styles.listScrollButton, styles.listScrollButtonLeft, 'elevated')}
          disabled={!canScrollLeft}
          onClick={() => scrollBy('left')}
        >
          <ArrowForwardIcon aria-hidden style={{ transform: 'rotate(180deg)' }} />
        </IconButton>
      )}
      {isOverflowing && (
        <IconButton
          aria-label={t('Tabs rechts scrollen')}
          className={classNames(styles.listScrollButton, styles.listScrollButtonRight, 'elevated')}
          disabled={!canScrollRight}
          onClick={() => scrollBy('right')}
        >
          <ArrowForwardIcon aria-hidden />
        </IconButton>
      )}
    </div>
  );
};

export type TabsTriggerProps = ComponentProps<typeof TabsPrimitive.Tab>;

export const TabsTrigger: FC<TabsTriggerProps> = ({ className, ...props }) => (
  <TabsPrimitive.Tab className={classNames(styles.trigger, className)} data-slot="tabs-trigger" {...props} />
);

export type TabsContentProps = ComponentProps<typeof TabsPrimitive.Panel>;

export const TabsContent: FC<TabsContentProps> = ({ className, ...props }) => (
  <TabsPrimitive.Panel className={classNames(styles.content, className)} data-slot="tabs-content" {...props} />
);
